import React, { useState, useEffect } from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import Button from '@atlaskit/button';
import Form, { Field } from '@atlaskit/form';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import TextArea from '@atlaskit/textarea';
import { N500 } from '@atlaskit/theme/colors';
import Select from '@atlaskit/select';
import { FeedbackTypeEnum } from './constants';
import './feedback-form.scss';

interface FormFields {
  optionSelected?: string;
  summary: string;
  description: string;
  additionalOption1?: boolean;
  additionalOption2?: boolean;
}

interface OptionType {
  label: string;
  value: FeedbackTypeEnum;
}

interface FeedbackFormProps {
  feedbackTitle?: React.ReactText;
  showSummary?: boolean;
  showCheckbox1?: boolean;
  showCheckbox2?: boolean;
  defaultChecked1?: boolean;
  defaultChecked2?: boolean;
  showTypeField?: boolean;
  feedbackTitleDetails?: React.ReactChild;
  additionalOptionLabel1?: string;
  additionalOptionLabel2?: string;
  summaryPlaceholder?: string;
  descriptionPlaceholder?: string;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  feedbackGroupLabels?: OptionType[];
  onClose: () => void;
  onSubmit: (formValues: FormFields) => Promise<void>;
  customContent?: React.ReactChild;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  feedbackTitle = 'Feedback form title',
  showSummary = false,
  showCheckbox1 = false,
  showCheckbox2 = false,
  defaultChecked1 = false,
  defaultChecked2 = false,
  showTypeField = false,
  additionalOptionLabel1 = 'checkbox1 label',
  additionalOptionLabel2 = 'checkbox2 label',
  summaryPlaceholder = 'Please enter a summary',
  descriptionPlaceholder = 'Please enter your feedback',
  submitButtonLabel = 'Submit',
  cancelButtonLabel = 'Cancel',
  feedbackTitleDetails,
  feedbackGroupLabels = [],
  onClose,
  onSubmit,
  customContent,
}) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [type, setType] = useState<FormFields['optionSelected']>(FeedbackTypeEnum.EMPTY);
  const [formValues, setFormValues] = useState<FormFields>({
    optionSelected: FeedbackTypeEnum.EMPTY,
    summary: '',
    description: '',
    additionalOption1: defaultChecked1,
    additionalOption2: defaultChecked2,
  });
  const isTypeSelected = () => type !== FeedbackTypeEnum.EMPTY;
  const canShowTextField = isTypeSelected() || !showTypeField;

  useEffect(() => {
    const { summary, description } = formValues;
    if (showSummary || type === FeedbackTypeEnum.BUG) {
      setIsSubmitDisabled(!(summary && description));
    } else {
      setIsSubmitDisabled(!description);
    }
  }, [formValues, showSummary, type]);

  const handleFieldChange = (field: keyof FormFields, value: any) => {
    setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
  };

  return (
    <Modal onClose={onClose}>
      <Form
        onSubmit={async () => {
          setIsSubmitting(true);
          await onSubmit(formValues);
          setIsSubmitting(false);
        }}
      >
        {({ formProps }) => (
          <form {...formProps} className="feedback-form-modal">
            <ModalHeader>
              <ModalTitle>{feedbackTitle}</ModalTitle>
              <Button spacing={'none'} tabIndex={-1} onClick={onClose} appearance={'subtle'}>
                <EditorCloseIcon label="Close Modal" primaryColor={N500} />
              </Button>
            </ModalHeader>
            <ModalBody>
              {feedbackTitleDetails}
              {showTypeField && (
                <Field name="feedbackGroupLabels">
                  {() => (
                    <Select<OptionType>
                      className="feedback-options"
                      onChange={(option) => {
                        if (!option || option instanceof Array) {
                          return;
                        }
                        setType(option.value);
                        handleFieldChange('optionSelected', option.value);
                      }}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 510,
                        }),
                      }}
                      defaultValue={{ label: 'I want to...', value: FeedbackTypeEnum.EMPTY }}
                      options={feedbackGroupLabels}
                    />
                  )}
                </Field>
              )}
              {canShowTextField && (
                <>
                  {(showSummary || type === FeedbackTypeEnum.BUG) && (
                    <Field name="summary" label="Summary" isRequired id="feedback-summary">
                      {({ fieldProps }) => (
                        <TextArea
                          {...fieldProps}
                          name="summary"
                          isCompact={true}
                          placeholder={summaryPlaceholder}
                          onChange={(e) => handleFieldChange('summary', e.currentTarget.value)}
                        />
                      )}
                    </Field>
                  )}
                  <Field
                    name="description"
                    label="Let us know what you'd like to improve"
                    isRequired
                    id="feedback-description"
                  >
                    {({ fieldProps }) => (
                      <TextArea
                        {...fieldProps}
                        name="description"
                        minimumRows={6}
                        placeholder={descriptionPlaceholder}
                        onChange={(e) => handleFieldChange('description', e.currentTarget.value)}
                      />
                    )}
                  </Field>
                  {customContent}
                  {showCheckbox1 && (
                    <Field name="additionalOption1">
                      {({ fieldProps }) => (
                        <Checkbox
                          {...fieldProps}
                          label={additionalOptionLabel1}
                          defaultChecked={defaultChecked1}
                          onChange={(e) => handleFieldChange('additionalOption1', e.currentTarget.checked)}
                          testId="additional-option-1"
                        />
                      )}
                    </Field>
                  )}
                  {showCheckbox2 && (type === FeedbackTypeEnum.BUG || type === FeedbackTypeEnum.EMPTY) && (
                    <Field name="additionalOption2">
                      {({ fieldProps }) => (
                        <Checkbox
                          {...fieldProps}
                          label={additionalOptionLabel2}
                          defaultChecked={defaultChecked2}
                          onChange={(e) => handleFieldChange('additionalOption2', e.currentTarget.checked)}
                          testId="additional-option-2"
                        />
                      )}
                    </Field>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={onClose}>
                {cancelButtonLabel}
              </Button>
              <Button
                type="submit"
                appearance="primary"
                isDisabled={isSubmitDisabled || isSubmitting}
                testId="feedbackCollectorSubmitBtn"
              >
                {submitButtonLabel}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};
